import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "U-BOOT U-96",
    description:
      "U-BOOT U-96 is a large-scale brick-built recreation of the legendary German Type VIIC submarine, designed for collectors, military enthusiasts, and experienced builders. With 3,565+ pieces and a 1:72 scale, this impressive model combines realistic proportions, detailed construction, and a highly developed interior.\n\nThe completed model measures approximately 96.2 cm in length, making it a spectacular display piece. Its long hull is packed with structural and interior details, while the modular construction allows selected sections to be quickly removed to reveal the inside of the submarine.\n\nThe interior features detailed crew areas, equipment, control elements, gauges, mechanisms, and other authentic-looking details. The construction is designed not only to look impressive from the outside but also to reward builders who enjoy discovering the engineering hidden inside the model.\n\nSeveral functional elements further enhance the display experience, including rotating and adjustable components, removable sections, and interchangeable torpedo-hatch configurations. The model can be presented in different configurations depending on your preferred display style.\n\nWith its enormous size, 1:72 scale, detailed interior, and modular construction, U-BOOT U-96 is an outstanding centerpiece for any military or historical collection.",
    highlightsTitle: "Product Features",
    features: [
      "Brand: 12GO",
      "Set Number: 98015",
      "Model: U-BOOT U-96",
      "Pieces: 3,565+",
      "Scale: 1:72",
      "Dimensions: approx. 96.2 × 22.1 × 10.5 cm",
      "Large-scale submarine model",
      "Highly detailed exterior and interior",
      "Modular quick-release construction",
      "Detailed crew and equipment areas",
      "Rotating and adjustable components",
      "Replaceable torpedo hatch configurations",
      "Display stand included",
      "Designed for experienced builders and collectors",
    ],
    specsTitle: "Specifications",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Set Number:</strong> 98015</li>",
      "<li><strong>Model:</strong> U-BOOT U-96</li>",
      "<li><strong>Pieces:</strong> 3,565+</li>",
      "<li><strong>Scale:</strong> 1:72</li>",
      "<li><strong>Dimensions:</strong> approx. 96.2 × 22.1 × 10.5 cm</li>",
    ],
  },
  pl: {
    title: "U-BOOT U-96",
    description:
      "U-BOOT U-96 to imponujący model niemieckiego okrętu podwodnego w dużej skali, stworzony z myślą o kolekcjonerach, miłośnikach militariów oraz doświadczonych konstruktorach. Zestaw składa się z ponad 3565 elementów i został wykonany w skali 1:72, oferując połączenie realistycznego wyglądu, dużych rozmiarów i niezwykle szczegółowej konstrukcji.\n\nPo złożeniu model ma około 96,2 cm długości, dzięki czemu stanowi wyjątkowy element ekspozycji. Długi kadłub kryje liczne detale konstrukcyjne oraz rozbudowane wnętrze, a modułowa budowa pozwala na szybki demontaż wybranych sekcji i zajrzenie do środka okrętu.\n\nWnętrze zostało szczegółowo odwzorowane i obejmuje między innymi przedziały załogi, wyposażenie, elementy sterujące, przyrządy, mechanizmy oraz wiele detali inspirowanych rzeczywistą konstrukcją okrętu podwodnego.\n\nModel oferuje również funkcjonalne elementy, takie jak obracane i regulowane komponenty, zdejmowane sekcje oraz wymienne konfiguracje włazów torpedowych. Dzięki temu można przygotować model do ekspozycji w wybranej konfiguracji.\n\nDzięki ogromnym rozmiarom, skali 1:72, szczegółowemu wnętrzu i modułowej konstrukcji U-BOOT U-96 może stać się centralnym punktem kolekcji militarnej lub historycznej.",
    highlightsTitle: "Cechy produktu",
    features: [
      "Marka: 12GO",
      "Numer zestawu: 98015",
      "Model: U-BOOT U-96",
      "Liczba elementów: 3565+",
      "Skala: 1:72",
      "Wymiary: ok. 96,2 × 22,1 × 10,5 cm",
      "Duży model okrętu podwodnego",
      "Bardzo szczegółowe wnętrze i kadłub",
      "Modułowa konstrukcja z szybkim demontażem",
      "Szczegółowe przedziały załogi i wyposażenie",
      "Obracane i regulowane elementy",
      "Wymienne konfiguracje włazów torpedowych",
      "Podstawka ekspozycyjna",
      "Przeznaczony dla doświadczonych konstruktorów i kolekcjonerów",
    ],
    specsTitle: "Specyfikacja",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Numer zestawu:</strong> 98015</li>",
      "<li><strong>Model:</strong> U-BOOT U-96</li>",
      "<li><strong>Liczba elementów:</strong> 3565+</li>",
      "<li><strong>Skala:</strong> 1:72</li>",
      "<li><strong>Wymiary:</strong> ok. 96,2 × 22,1 × 10,5 cm</li>",
    ],
  },
  es: {
    title: "U-BOOT U-96",
    description:
      "U-BOOT U-96 es una impresionante recreación a gran escala de un submarino alemán, diseñada para coleccionistas, aficionados a la historia militar y constructores experimentados. Con más de 3.565 piezas y una escala 1:72, el modelo combina proporciones realistas, una construcción detallada y un interior cuidadosamente desarrollado.\n\nUna vez terminado, el submarino alcanza aproximadamente 96,2 cm de longitud, convirtiéndose en una pieza de exposición espectacular. Su largo casco está lleno de detalles estructurales y elementos interiores, mientras que el diseño modular permite retirar rápidamente determinadas secciones para mostrar el interior.\n\nEl interior incluye zonas de la tripulación, equipamiento, instrumentos, mecanismos y numerosos detalles inspirados en la construcción de un submarino real.\n\nTambién incorpora varios elementos funcionales, como componentes giratorios y ajustables, secciones desmontables y configuraciones intercambiables de las escotillas de torpedos. Esto permite adaptar el modelo a diferentes formas de exposición.\n\nGracias a su enorme tamaño, escala 1:72, interior detallado y construcción modular, U-BOOT U-96 es una pieza central ideal para cualquier colección militar o histórica.",
    highlightsTitle: "Características",
    features: [
      "Marca: 12GO",
      "Número de set: 98015",
      "Modelo: U-BOOT U-96",
      "Piezas: 3.565+",
      "Escala: 1:72",
      "Dimensiones: aprox. 96,2 × 22,1 × 10,5 cm",
      "Gran modelo de submarino",
      "Exterior e interior muy detallados",
      "Construcción modular de desmontaje rápido",
      "Compartimentos y equipamiento detallados",
      "Elementos giratorios y ajustables",
      "Escotillas de torpedos intercambiables",
      "Soporte para exposición",
      "Ideal para constructores experimentados y coleccionistas",
    ],
    specsTitle: "Especificaciones",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Número de set:</strong> 98015</li>",
      "<li><strong>Modelo:</strong> U-BOOT U-96</li>",
      "<li><strong>Piezas:</strong> 3.565+</li>",
      "<li><strong>Escala:</strong> 1:72</li>",
      "<li><strong>Dimensiones:</strong> aprox. 96,2 × 22,1 × 10,5 cm</li>",
    ],
  },
  fr: {
    title: "U-BOOT U-96",
    description:
      "U-BOOT U-96 est une impressionnante reproduction à grande échelle d'un sous-marin allemand, conçue pour les collectionneurs, les passionnés d'histoire militaire et les constructeurs expérimentés. Avec plus de 3 565 pièces et une échelle 1:72, ce modèle associe des proportions réalistes, une construction très détaillée et un intérieur particulièrement travaillé.\n\nUne fois assemblé, le modèle mesure environ 96,2 cm de long, ce qui en fait une pièce d'exposition spectaculaire. Sa longue coque renferme de nombreux détails structurels et intérieurs, tandis que sa conception modulaire permet de retirer rapidement certaines sections afin de découvrir l'intérieur.\n\nL'intérieur présente notamment les espaces de l'équipage, différents équipements, instruments, mécanismes et de nombreux détails inspirés d'un véritable sous-marin.\n\nPlusieurs éléments fonctionnels renforcent encore le réalisme du modèle, notamment des composants orientables et réglables, des sections amovibles ainsi que des configurations interchangeables pour les trappes de torpilles.\n\nAvec son impressionnante longueur, son échelle 1:72, son intérieur détaillé et sa conception modulaire, U-BOOT U-96 est une pièce maîtresse pour toute collection militaire ou historique.",
    highlightsTitle: "Caractéristiques",
    features: [
      "Marque : 12GO",
      "Référence : 98015",
      "Modèle : U-BOOT U-96",
      "Nombre de pièces : 3 565+",
      "Échelle : 1:72",
      "Dimensions : env. 96,2 × 22,1 × 10,5 cm",
      "Grand modèle de sous-marin",
      "Extérieur et intérieur très détaillés",
      "Construction modulaire à démontage rapide",
      "Compartiments et équipements détaillés",
      "Éléments orientables et réglables",
      "Trappes de torpilles interchangeables",
      "Support d'exposition",
      "Idéal pour les constructeurs expérimentés et les collectionneurs",
    ],
    specsTitle: "Spécifications",
    specs: [
      "<li><strong>Marque :</strong> 12GO</li>",
      "<li><strong>Référence :</strong> 98015</li>",
      "<li><strong>Modèle :</strong> U-BOOT U-96</li>",
      "<li><strong>Nombre de pièces :</strong> 3 565+</li>",
      "<li><strong>Échelle :</strong> 1:72</li>",
      "<li><strong>Dimensions :</strong> env. 96,2 × 22,1 × 10,5 cm</li>",
    ],
  },
  nl: {
    title: "U-BOOT U-96",
    description:
      "U-BOOT U-96 is een indrukwekkend groot bouwstenenmodel van een Duitse onderzeeër, ontworpen voor verzamelaars, liefhebbers van militaire geschiedenis en ervaren bouwers. Met meer dan 3.565 onderdelen en een schaal van 1:72 combineert dit model realistische verhoudingen met een zeer gedetailleerde buitenkant en een uitgebreid interieur.\n\nNa voltooiing is het model ongeveer 96,2 cm lang, waardoor het een opvallend middelpunt van elke collectie vormt. De lange romp bevat talrijke structurele en interieurdetails. Dankzij het modulaire ontwerp kunnen bepaalde delen snel worden verwijderd om het interieur zichtbaar te maken.\n\nHet interieur bevat onder andere bemanningsruimtes, uitrusting, instrumenten, mechanismen en talrijke details die zijn geïnspireerd op echte onderzeeboten.\n\nDaarnaast beschikt het model over verschillende beweegbare en aanpasbare onderdelen, waaronder draaibare componenten, uitneembare secties en verwisselbare configuraties voor de torpedoluiken.\n\nDankzij zijn indrukwekkende formaat, 1:72-schaal, gedetailleerde interieur en modulaire constructie is U-BOOT U-96 een echte blikvanger voor iedere militaire of historische verzameling.",
    highlightsTitle: "Productkenmerken",
    features: [
      "Merk: 12GO",
      "Setnummer: 98015",
      "Model: U-BOOT U-96",
      "Onderdelen: 3.565+",
      "Schaal: 1:72",
      "Afmetingen: ca. 96,2 × 22,1 × 10,5 cm",
      "Groot onderzeeërmodel",
      "Zeer gedetailleerd interieur en exterieur",
      "Modulair ontwerp met snelle demontage",
      "Gedetailleerde bemanningsruimtes en uitrusting",
      "Draaibare en verstelbare onderdelen",
      "Verwisselbare torpedoluikconfiguraties",
      "Displaystandaard",
      "Ideaal voor ervaren bouwers en verzamelaars",
    ],
    specsTitle: "Specificaties",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 98015</li>",
      "<li><strong>Model:</strong> U-BOOT U-96</li>",
      "<li><strong>Onderdelen:</strong> 3.565+</li>",
      "<li><strong>Schaal:</strong> 1:72</li>",
      "<li><strong>Afmetingen:</strong> ca. 96,2 × 22,1 × 10,5 cm</li>",
    ],
  },
  de: {
    title: "U-BOOT U-96",
    description:
      "U-BOOT U-96 ist ein beeindruckendes Klemmbausteinmodell eines deutschen U-Bootes im großen Maßstab. Es richtet sich an Sammler, Militär- und Geschichtsinteressierte sowie erfahrene Modellbauer. Mit mehr als 3.565 Teilen und einem Maßstab von 1:72 verbindet das Modell realistische Proportionen mit einer äußerst detaillierten Konstruktion.\n\nDas fertige Modell ist etwa 96,2 cm lang und wird dadurch zu einem echten Blickfang in jeder Sammlung. Der lange Rumpf verfügt über zahlreiche Konstruktionsdetails und ein umfangreich gestaltetes Innenleben. Durch die modulare Bauweise können ausgewählte Bereiche schnell abgenommen werden, um den Innenraum zu präsentieren.\n\nDas Innere umfasst unter anderem Besatzungsbereiche, Ausrüstung, Instrumente, Mechanismen und zahlreiche authentisch wirkende Details.\n\nFür zusätzliche Funktionalität sorgen drehbare und verstellbare Elemente, abnehmbare Sektionen sowie austauschbare Konfigurationen der Torpedoluken. Dadurch kann das Modell individuell für die Präsentation gestaltet werden.\n\nMit seiner beeindruckenden Größe, dem Maßstab 1:72, dem detaillierten Innenraum und der modularen Konstruktion ist U-BOOT U-96 ein außergewöhnliches Ausstellungsstück für jede Militär- oder historische Sammlung.",
    highlightsTitle: "Produkteigenschaften",
    features: [
      "Marke: 12GO",
      "Setnummer: 98015",
      "Modell: U-BOOT U-96",
      "Teile: 3.565+",
      "Maßstab: 1:72",
      "Abmessungen: ca. 96,2 × 22,1 × 10,5 cm",
      "Großes U-Boot-Modell",
      "Hochdetaillierter Innen- und Außenbereich",
      "Modulare Schnellverschluss-Konstruktion",
      "Detaillierte Besatzungsbereiche und Ausrüstung",
      "Drehbare und verstellbare Elemente",
      "Austauschbare Torpedoluken-Konfigurationen",
      "Präsentationsständer",
      "Ideal für erfahrene Modellbauer und Sammler",
    ],
    specsTitle: "Spezifikationen",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 98015</li>",
      "<li><strong>Modell:</strong> U-BOOT U-96</li>",
      "<li><strong>Teile:</strong> 3.565+</li>",
      "<li><strong>Maßstab:</strong> 1:72</li>",
      "<li><strong>Abmessungen:</strong> ca. 96,2 × 22,1 × 10,5 cm</li>",
    ],
  },
  it: {
    title: "U-BOOT U-96",
    description:
      "U-BOOT U-96 è un imponente modello in mattoncini di un sottomarino tedesco in grande scala, progettato per collezionisti, appassionati di storia militare e costruttori esperti. Con oltre 3.565 pezzi e una scala 1:72, il modello combina proporzioni realistiche, costruzione dettagliata e un interno particolarmente ricco.\n\nUna volta completato, il modello raggiunge circa 96,2 cm di lunghezza, diventando un elemento di grande impatto per l'esposizione. Lo scafo contiene numerosi dettagli strutturali e interni, mentre la costruzione modulare consente di rimuovere rapidamente alcune sezioni per mostrare l'interno del sottomarino.\n\nL'interno comprende aree dell'equipaggio, equipaggiamenti, strumenti, meccanismi e numerosi dettagli ispirati alla struttura di un vero sottomarino.\n\nSono inoltre presenti diversi elementi funzionali, tra cui componenti girevoli e regolabili, sezioni rimovibili e configurazioni intercambiabili dei portelli dei siluri.\n\nGrazie alle sue grandi dimensioni, alla scala 1:72, all'interno dettagliato e alla costruzione modulare, U-BOOT U-96 è un eccezionale pezzo da esposizione per qualsiasi collezione militare o storica.",
    highlightsTitle: "Caratteristiche",
    features: [
      "Marca: 12GO",
      "Numero set: 98015",
      "Modello: U-BOOT U-96",
      "Pezzi: 3.565+",
      "Scala: 1:72",
      "Dimensioni: circa 96,2 × 22,1 × 10,5 cm",
      "Grande modello di sottomarino",
      "Esterno e interno altamente dettagliati",
      "Costruzione modulare a sgancio rapido",
      "Aree dell'equipaggio ed equipaggiamenti dettagliati",
      "Elementi girevoli e regolabili",
      "Configurazioni intercambiabili dei portelli dei siluri",
      "Supporto da esposizione",
      "Ideale per costruttori esperti e collezionisti",
    ],
    specsTitle: "Specifiche",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Numero set:</strong> 98015</li>",
      "<li><strong>Modello:</strong> U-BOOT U-96</li>",
      "<li><strong>Pezzi:</strong> 3.565+</li>",
      "<li><strong>Scala:</strong> 1:72</li>",
      "<li><strong>Dimensioni:</strong> circa 96,2 × 22,1 × 10,5 cm</li>",
    ],
  },
  ru: {
    title: "U-BOOT U-96",
    description:
      "U-BOOT U-96 — это большой детализированный сборный модельный набор, посвящённый немецкой подводной лодке. Модель создана для коллекционеров, любителей военной истории и опытных конструкторов. В наборе более 3 565 деталей, а масштаб составляет 1:72.\n\nПосле сборки модель имеет длину около 96,2 см, благодаря чему становится эффектным экспонатом для коллекции. Длинный корпус наполнен конструктивными деталями и тщательно проработанным интерьером. Модульная конструкция позволяет быстро снимать отдельные секции и демонстрировать внутреннее устройство подводной лодки.\n\nВнутри представлены отсеки экипажа, оборудование, приборы, механизмы и множество деталей, вдохновлённых конструкцией настоящих подводных лодок.\n\nДополнительную функциональность обеспечивают поворотные и регулируемые элементы, съёмные секции и сменные варианты торпедных люков.\n\nБлагодаря внушительным размерам, масштабу 1:72, детализированному интерьеру и модульной конструкции U-BOOT U-96 станет выразительным центральным элементом любой военно-исторической коллекции.",
    highlightsTitle: "Характеристики",
    features: [
      "Бренд: 12GO",
      "Номер набора: 98015",
      "Модель: U-BOOT U-96",
      "Количество деталей: 3 565+",
      "Масштаб: 1:72",
      "Размеры: ок. 96,2 × 22,1 × 10,5 см",
      "Большая модель подводной лодки",
      "Детализированные внешний вид и интерьер",
      "Модульная конструкция с быстрым демонтажем",
      "Детализированные отсеки экипажа и оборудование",
      "Поворотные и регулируемые элементы",
      "Сменные варианты торпедных люков",
      "Подставка для экспонирования",
      "Подходит для опытных конструкторов и коллекционеров",
    ],
    specsTitle: "Спецификация",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Номер набора:</strong> 98015</li>",
      "<li><strong>Модель:</strong> U-BOOT U-96</li>",
      "<li><strong>Количество деталей:</strong> 3 565+</li>",
      "<li><strong>Масштаб:</strong> 1:72</li>",
      "<li><strong>Размеры:</strong> ок. 96,2 × 22,1 × 10,5 см</li>",
    ],
  },
  zh: {
    title: "U-BOOT U-96",
    description:
      "U-BOOT U-96 是一款大比例德国潜艇积木模型，专为收藏家、军事历史爱好者以及经验丰富的积木玩家设计。整套包含 3565+ 个零件，采用 1:72 比例，在还原整体外观的同时，对内部结构也进行了丰富的细节刻画。\n\n模型完成后长度约为 96.2 cm，拥有非常出色的展示效果。细长的艇身包含大量结构细节，并采用模块化设计，可快速拆卸部分区域，从而展示潜艇内部结构。\n\n内部还原了乘员区域、设备、仪表、机械结构以及各种细节部件，让模型不仅拥有出色的外观，也具备丰富的内部观赏性。\n\n模型还配备多种可动和可调节结构，包括可旋转部件、可拆卸模块以及可替换的鱼雷舱盖配置，能够根据不同需求进行展示。\n\n凭借超大尺寸、1:72 比例、丰富的内部结构以及模块化设计，U-BOOT U-96 非常适合作为军事或历史主题收藏中的核心展示作品。",
    highlightsTitle: "产品特点",
    features: [
      "品牌： 12GO",
      "产品编号： 98015",
      "型号： U-BOOT U-96",
      "积木数量： 3565+ 块",
      "比例： 1:72",
      "成品尺寸： 约 96.2 × 22.1 × 10.5 cm",
      "大比例潜艇模型",
      "高度还原的外观与内部结构",
      "模块化快速拆装设计",
      "详细的乘员舱与设备",
      "多种旋转及可调节结构",
      "可替换鱼雷舱盖配置",
      "配备展示底座",
      "适合资深玩家及收藏家",
    ],
    specsTitle: "规格",
    specs: [
      "<li><strong>品牌：</strong> 12GO</li>",
      "<li><strong>产品编号：</strong> 98015</li>",
      "<li><strong>型号：</strong> U-BOOT U-96</li>",
      "<li><strong>积木数量：</strong> 3565+ 块</li>",
      "<li><strong>比例：</strong> 1:72</li>",
      "<li><strong>成品尺寸：</strong> 约 96.2 × 22.1 × 10.5 cm</li>",
    ],
  },
};

type LocaleKey = keyof typeof COPY;

function buildLocaleHtml(locale: LocaleKey): string {
  const c = COPY[locale] ?? COPY.en;
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}`;
}

export const RICH_PRODUCT_COPY_98015: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
